import styled from 'threads5/themes/styled';
import { Box, Stack } from 'src/components-v2/Layout';

// Matches the Framer prototype's accordion spring
// ({ type: 'spring', stiffness: 500, damping: 60, mass: 1 }): overdamped,
// settles in ~0.5s. The bezier fits the exact spring curve within 2%.
const SPRING_DURATION = '0.5s';
const SPRING = `${SPRING_DURATION} cubic-bezier(0.23, 0.41, 0.07, 0.96)`;

export const Strip = styled('div')({
  height: '3px',
  background:
    'linear-gradient(90deg, #78BEFC 0%, #DAACF7 52.15%, #98AFFB 100%)',
  width: '100%',
});

export const Question = styled('h3')(({ theme }) => {
  return {
    color: theme.palette['web3/component/faq/text/question'],
    fontFamily: theme.typography.cern,
    fontSize: '1.8rem',
    fontWeight: 600,
    lineHeight: '150%',
    letterSpacing: '-0.3px',
    margin: 0,
    [theme.breakpoints.up('md')]: {
      fontSize: '2rem',
    },
  };
});

export const Answer = styled('p')(({ theme }) => {
  const baseStyles = {
    color: theme.palette['web3/component/faq/text/answer'],
    fontFamily: theme.typography.cern + ' !important',
    fontSize: '1.6rem',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '150%',
    margin: 0,
  };

  return {
    ...baseStyles,
    '& p': baseStyles,
    '& ul': {
      marginTop: '8px',
      marginBottom: '8px',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '1.8rem',
      '& p': {
        fontSize: '1.8rem',
      },
    },
  };
});

export const StyledStack = styled(Stack, {
  shouldForwardProp: (prop) => {
    return prop !== 'isExpanded';
  },
})<{ isExpanded: boolean }>(({ theme, isExpanded }) => {
  return {
    padding: isExpanded ? '40px 24px' : theme.spacing(3),
    transition: 'max-height 0.3s ease, padding 0.3s ease, background 0.3s ease',
    maxHeight: isExpanded ? '1000px' : '100px',
    background: isExpanded
      ? theme.palette['web3/component/faq/background']
      : 'transparent',
    borderRadius: '12px 12px 0 0',
    '&:focus-visible': {
      outlineWidth: '2px',
      outlineColor: theme.palette['web3/component/button/outline'],
      outlineStyle: 'solid',
      outlineOffset: '3px',
    },
  };
});

export const Divider = styled('div', {
  shouldForwardProp: (prop) => {
    return prop !== 'isVisible';
  },
})<{ isVisible: boolean }>(({ theme, isVisible }) => {
  return {
    transition: 'opacity 0.1s ease, height 0.1s ease',
    opacity: isVisible ? 1 : 0,
    height: isVisible ? '1px' : '0px',
    background: theme.palette['web3/component/faq/divider'],
  };
});

export const FaqRowContainer = styled(Box, {
  shouldForwardProp: (prop) => {
    return prop !== 'isExpanded';
  },
})<{ isExpanded: boolean }>(({ theme, isExpanded }) => {
  return {
    width: '100%',
    position: 'relative',
    '.expand-icon': {
      color: isExpanded
        ? theme.palette['web3/component/faq/icon/color-expanded']
        : theme.palette['web3/component/faq/icon/color'],
      transition: 'color 0.3s ease',
    },
    '.faq-row-background': {
      background: theme.palette['web3/component/faq/background/hover'],
      opacity: 0,
      transition: 'opacity 0.3s ease',
    },
    '&:hover': {
      '.faq-row-background': {
        opacity: 0.3,
      },
      '.expand-icon': {
        color: theme.palette['web3/component/faq/icon/color-hover'],
      },
    },
    ...(isExpanded
      ? {
          boxShadow:
            '0 152px 42px 0 rgba(2, 41, 76, 0.00), 0 97px 39px 0 rgba(2, 41, 76, 0.01), 0 55px 33px 0 rgba(2, 41, 76, 0.02), 0 24px 24px 0 rgba(2, 41, 76, 0.04), 0 6px 13px 0 rgba(2, 41, 76, 0.04)',
          backdropFilter: 'blur(2px)',
        }
      : {}),
  };
});

export const FaqDetails = styled('details')(({ theme }) => {
  return {
    interpolateSize: 'allow-keywords',
    borderBottom: `1px solid ${theme.palette['web3/component/faq/divider']}`,
    '&::details-content': {
      transition: `block-size ${SPRING}, content-visibility ${SPRING_DURATION} allow-discrete`,
      overflow: 'hidden',
      blockSize: '0',
    },
    '&[open]': {
      boxShadow:
        '0 152px 42px 0 rgba(2, 41, 76, 0.00), 0 97px 39px 0 rgba(2, 41, 76, 0.01), 0 55px 33px 0 rgba(2, 41, 76, 0.02), 0 24px 24px 0 rgba(2, 41, 76, 0.04), 0 6px 13px 0 rgba(2, 41, 76, 0.04)',
      backdropFilter: 'blur(2px)',
      background: theme.palette['web3/component/faq/background'],
      borderRadius: '12px 12px 0 0',
      '.faq-icon-bar': {
        background: theme.palette['web3/component/faq/icon/color-expanded'],
      },
      // Both bars settle horizontal (a minus), spinning clockwise. The
      // prototype's measured sweep is 270°/180°, but its icon translates
      // while spinning (the list re-centers), which masks the rotation; in
      // our static row the same sweep reads as a full spin, so the vertical
      // bar does a quarter turn instead to match the prototype's *perceived*
      // motion.
      '.faq-icon-bar--horizontal': {
        rotate: '180deg',
      },
      '.faq-icon-bar--vertical': {
        rotate: '90deg',
      },
    },
    '&[open]::details-content': {
      blockSize: 'auto',
    },
    // Hide border on element that comes before an open one
    '&:has(+ &[open])': {
      borderBottom: '1px solid transparent',
    },
    '@media (prefers-reduced-motion: reduce)': {
      '&::details-content': {
        transition: 'none',
      },
    },
  };
});

export const FaqDetailsSummary = styled('summary')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    cursor: 'pointer',
    padding: '29px 20px 29px 24px',
    position: 'relative',
    transition: 'opacity 0.3s ease',
    h3: {
      color: theme.palette['web3/component/faq/text/question'],
      fontFamily: theme.typography.cern,
      fontSize: '1.8rem',
      fontWeight: 600,
      lineHeight: '150%',
      letterSpacing: '-0.3px',
      margin: 0,
      [theme.breakpoints.up('md')]: {
        fontSize: '2rem',
      },
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: theme.palette['web3/component/faq/background/hover'],
      opacity: 0,
      transition: `opacity ${SPRING}`,
      pointerEvents: 'none',
    },
    '&:hover': {
      '&::before': {
        opacity: 0.3,
      },
      '.faq-icon-bar': {
        background: theme.palette['web3/component/faq/icon/color-hover'],
      },
    },
    // Keep the open-state appearance stable while hovered
    'details[open] &:hover::before': {
      opacity: 0,
    },
    'details[open] &:hover .faq-icon-bar': {
      background: theme.palette['web3/component/faq/icon/color-expanded'],
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '&::before': {
        transition: 'none',
      },
    },
  };
});

export const FaqDetailsContent = styled('div')(({ theme }) => {
  const baseStyles = {
    color: theme.palette['web3/component/faq/text/answer'],
    fontFamily: theme.typography.cern + ' !important',
    fontSize: '1.6rem',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '150%',
    margin: 0,
    padding: '0 20px 29px 24px',
    marginTop: '-5px',
  };

  return {
    opacity: 0,
    transition: `opacity ${SPRING}`,
    'details[open] &': {
      opacity: 1,
    },
    '& p': baseStyles,
    '& ul': {
      marginLeft: '42px',
      marginTop: '-9px',
      '& li p': {
        margin: '0 0 0 4px',
        padding: '0 0 16px 4px',
      },
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '1.8rem',
      '& p': {
        fontSize: '1.8rem',
      },
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  };
});

export const FaqIcons = styled('div')(({ theme }) => {
  return {
    position: 'relative',
    width: '20px',
    height: '20px',
    flexShrink: 0,
    // The plus is drawn as two axis-aligned bars (like the Framer prototype)
    // so each can rotate independently and collapse into a minus when
    // expanded; browsers without `rotate` support degrade to a static plus.
    '.faq-icon-bar': {
      position: 'absolute',
      // Explicit 0deg (not the `none` initial value) so the open-state
      // rotation has an interpolable start.
      rotate: '0deg',
      background: theme.palette['web3/component/faq/icon/color'],
      transition: `rotate ${SPRING}, background ${SPRING}`,
    },
    '.faq-icon-bar--vertical': {
      top: 'calc(50% - 9px)',
      left: 'calc(50% - 0.75px)',
      width: '1.5px',
      height: '18px',
    },
    '.faq-icon-bar--horizontal': {
      top: 'calc(50% - 0.75px)',
      left: 'calc(50% - 9px)',
      width: '18px',
      height: '1.5px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      '.faq-icon-bar': {
        transition: 'none',
      },
    },
  };
});
